import React, { useCallback, useMemo, useState } from 'react';

import { cn } from '@/utils/classname';

const CPE_FIELDS = [
  { key: 'vendor', label: 'Vendor', placeholder: 'apache' },
  { key: 'product', label: 'Product', placeholder: 'httpd' },
  { key: 'version', label: 'Version', placeholder: '2.4.50' },
  { key: 'update', label: 'Update', placeholder: '*' },
  { key: 'edition', label: 'Edition', placeholder: '*' },
  { key: 'language', label: 'Language', placeholder: '*' },
  { key: 'swEdition', label: 'SW Edition', placeholder: '*' },
  { key: 'targetSw', label: 'Target SW', placeholder: '*' },
  { key: 'targetHw', label: 'Target HW', placeholder: '*' },
  { key: 'other', label: 'Other', placeholder: '*' },
] as const;

type CpeField = (typeof CPE_FIELDS)[number]['key'];

type CpeFields = Record<CpeField, string>;

const DEFAULT_FIELDS: CpeFields = {
  vendor: '',
  product: '',
  version: '',
  update: '*',
  edition: '*',
  language: '*',
  swEdition: '*',
  targetSw: '*',
  targetHw: '*',
  other: '*',
};

function parseCpe(cpe: string): CpeFields {
  const trimmed = cpe.trim();
  if (!trimmed) return { ...DEFAULT_FIELDS };

  const parts = trimmed.split(':');

  // Handle cpe:2.3:part:vendor:product:version:update:edition:language:sw_edition:target_sw:target_hw:other
  // Index: 0   :1  :2   :3     :4      :5      :6     :7      :8       :9         :10       :11       :12
  if (parts.length >= 5 && parts[0] === 'cpe' && parts[1] === '2.3') {
    return {
      vendor: parts[3] || '',
      product: parts[4] || '',
      version: parts[5] || '*',
      update: parts[6] || '*',
      edition: parts[7] || '*',
      language: parts[8] || '*',
      swEdition: parts[9] || '*',
      targetSw: parts[10] || '*',
      targetHw: parts[11] || '*',
      other: parts[12] || '*',
    };
  }

  return { ...DEFAULT_FIELDS };
}

function buildCpe(fields: CpeFields): string {
  return [
    'cpe',
    '2.3',
    'a',
    fields.vendor || '*',
    fields.product || '*',
    fields.version || '*',
    fields.update || '*',
    fields.edition || '*',
    fields.language || '*',
    fields.swEdition || '*',
    fields.targetSw || '*',
    fields.targetHw || '*',
    fields.other || '*',
  ].join(':');
}

type Mode = 'raw' | 'builder';

interface CpeEditorProps {
  value: string;
  onChange: (cpe: string) => void;
  disabled?: boolean;
}

const inputClasses = cn(
  'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-1.5 text-sm',
  'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

export const CpeEditor: React.FC<CpeEditorProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [mode, setMode] = useState<Mode>('builder');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fields = useMemo(() => parseCpe(value), [value]);

  const handleFieldChange = useCallback(
    (key: CpeField, fieldValue: string) => {
      const updated = { ...fields, [key]: fieldValue };
      onChange(buildCpe(updated));
    },
    [fields, onChange]
  );

  const handleRawChange = useCallback(
    (raw: string) => {
      onChange(raw);
    },
    [onChange]
  );

  // Preview string shown below the builder
  const previewCpe = useMemo(() => buildCpe(fields), [fields]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-chariot-text-primary block text-sm font-medium">
          CPE String
        </label>
        <div className="bg-chariot-background-secondary inline-flex rounded-md p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setMode('builder')}
            disabled={disabled}
            className={cn(
              'rounded px-2.5 py-1 font-medium transition-colors',
              mode === 'builder'
                ? 'bg-chariot-background-primary text-chariot-text-primary shadow-sm'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            )}
          >
            Builder
          </button>
          <button
            type="button"
            onClick={() => setMode('raw')}
            disabled={disabled}
            className={cn(
              'rounded px-2.5 py-1 font-medium transition-colors',
              mode === 'raw'
                ? 'bg-chariot-background-primary text-chariot-text-primary shadow-sm'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            )}
          >
            Raw
          </button>
        </div>
      </div>

      {mode === 'raw' ? (
        <div>
          <input
            id="osint-cpe-input"
            type="text"
            value={value}
            onChange={e => handleRawChange(e.target.value)}
            placeholder="cpe:2.3:a:vendor:product:version:*:*:*:*:*:*:*"
            disabled={disabled}
            className={cn(inputClasses, 'font-mono')}
          />
          <p className="text-chariot-text-secondary mt-1.5 text-xs">
            Paste a full CPE 2.3 string (e.g.,
            cpe:2.3:a:apache:httpd:2.4.50:*:*:*:*:*:*:*)
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Primary fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-chariot-text-secondary mb-1 block text-xs">
                Vendor
              </label>
              <input
                type="text"
                value={fields.vendor}
                onChange={e => handleFieldChange('vendor', e.target.value)}
                placeholder="apache"
                disabled={disabled}
                className={inputClasses}
              />
            </div>
            <div>
              <label className="text-chariot-text-secondary mb-1 block text-xs">
                Product
              </label>
              <input
                type="text"
                value={fields.product}
                onChange={e => handleFieldChange('product', e.target.value)}
                placeholder="httpd"
                disabled={disabled}
                className={inputClasses}
              />
            </div>
            <div>
              <label className="text-chariot-text-secondary mb-1 block text-xs">
                Version
              </label>
              <input
                type="text"
                value={fields.version}
                onChange={e => handleFieldChange('version', e.target.value)}
                placeholder="2.4.50"
                disabled={disabled}
                className={inputClasses}
              />
            </div>
          </div>

          {/* Advanced fields toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs"
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced fields
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-3 gap-3">
              {CPE_FIELDS.filter(
                f => !['vendor', 'product', 'version'].includes(f.key)
              ).map(f => (
                <div key={f.key}>
                  <label className="text-chariot-text-secondary mb-1 block text-xs">
                    {f.label}
                  </label>
                  <input
                    type="text"
                    value={fields[f.key]}
                    onChange={e => handleFieldChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    disabled={disabled}
                    className={inputClasses}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Preview */}
          {previewCpe && (
            <div className="bg-chariot-background-secondary rounded-md px-3 py-2">
              <p className="text-chariot-text-secondary mb-0.5 text-[10px] tracking-wide uppercase">
                Preview
              </p>
              <p className="text-chariot-text-primary font-mono text-xs break-all">
                {previewCpe}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
